import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, Plus, SquarePen, Trash2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Permit } from '@/components/guards/Permit'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ListPagination } from '@/components/shared/ListPagination'
import { ListExportDialog } from '@/components/shared'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import {
  ContractStatusBadge,
  ClientNameWithProspect,
} from '@/components/shared/StatusBadges'
import { ContractListFilters } from '@/components/contracts/ContractListFilters'
import {
  ContractMetricsSection,
  type ContractMetricActions,
  type ContractMetricKey,
} from '@/components/contracts/ContractMetricsSection'
import { DeleteContractDialog } from '@/components/contracts/DeleteContractDialog'
import { TerminateContractDialog } from '@/components/contracts/TerminateContractDialog'
import { useTopBarActions } from '@/contexts/TopBarActionsContext'
import { useContractsList, useContractSummary } from '@/hooks/useContracts'
import { useUrlListParams } from '@/hooks/useUrlListParams'
import { contractsApi } from '@/services/contracts'
import { isContractEditable } from '@/lib/contracts'
import { formatCurrency, formatDate, monthsBetween } from '@/lib/format'
import { cn } from '@/lib/utils'
import {
  CONTRACT_FREQUENCY_LABELS,
  CONTRACT_STATUS_LABELS,
  type Contract,
  type ContractListQuery,
} from '@/types'

type Filters = {
  status?: string
  end_date_from?: string
  end_date_to?: string
  expiring?: string
}

// Figma 3396:160430 — TopNavi primary CTA (New Contract).
const topBarNewContractClass =
  'h-10 gap-2 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 py-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'

const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-[10px] font-medium uppercase leading-[14px] tracking-normal text-[#3d434e]'

const tdClass =
  'h-16 overflow-hidden border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm'

const actionBtnClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-1.5 text-[#272c36] transition-colors group-hover:bg-[#fbfbfc] hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'
const destructiveActionBtnClass =
  'hover:border-[#d63b33] hover:bg-[#fde9e8] hover:text-[#d63b33] group-hover:hover:bg-[#fde9e8]'
const contractActionsGroupClass =
  'ml-auto flex w-[112px] items-center justify-end gap-2'

/** Figma 3396:160472 column widths (9 columns, 1152px). */
const COL = {
  customer: 'w-[236px] min-w-[236px] text-left',
  title: 'w-[200px] min-w-[200px] text-left',
  amount: 'w-[100px] min-w-[100px] text-center',
  status: 'w-[120px] min-w-[120px] text-center',
  dueDate: 'w-[100px] min-w-[100px] text-left',
  frequency: 'w-[100px] min-w-[100px] text-left',
  duration: 'w-[100px] min-w-[100px] text-left',
  contractNo: 'w-[140px] min-w-[140px] text-left',
  actions: 'w-[144px] min-w-[112px] text-right',
} as const

const TABLE_MIN_WIDTH = 1208
const COLUMN_COUNT = 9

const EMPTY = '--'

function formatLocalDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Contract duration in months/years between start and end (2.8.1-026).
function durationLabel(
  start: string | null,
  end: string | null,
  t: TFunction
): string {
  if (!start || !end) return EMPTY
  const s = new Date(start)
  const e = new Date(end)
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return EMPTY
  const months = monthsBetween(s, e)
  if (months < 0) return EMPTY
  if (months >= 12 && months % 12 === 0) {
    const y = months / 12
    return t('list.duration.year', { count: y })
  }
  return t('list.duration.month', { count: months })
}

function frequencyLabel(frequency: string | null): string {
  if (!frequency) return EMPTY
  return CONTRACT_FREQUENCY_LABELS[frequency] ?? frequency
}

function ListStatusBadge({ contract }: { contract: Contract }) {
  return <ContractStatusBadge status={contract.status} />
}

// 2.8.1 Contract list — Figma 3396:160431.
export default function ContractsListPage() {
  const { t } = useTranslation('contracts')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const expiringOnly = searchParams.get('expiring') === '1'

  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useUrlListParams<Filters>({})

  const selectedStatuses = useMemo(
    () => (filters.status ? filters.status.split(',').filter(Boolean) : []),
    [filters.status]
  )
  const listQuery = useMemo<ContractListQuery>(() => {
    const { status: _status, ...rest } = query
    void _status
    return {
      ...rest,
      status: selectedStatuses.length ? selectedStatuses : undefined,
      expiring: expiringOnly || undefined,
    }
  }, [query, selectedStatuses, expiringOnly])
  const { data, isLoading, isError } = useContractsList(listQuery)
  const contracts = useMemo(() => data?.data ?? [], [data])
  const totalRows = data?.metadata?.total ?? contracts.length

  // Tenant-wide KPI tiles (Figma 3396:160432): counts + annual amounts.
  const { data: summary, isLoading: summaryLoading } = useContractSummary()
  const renewWithin30DaysRange = useMemo(() => {
    const today = new Date()
    const horizon = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + 30
    )

    return {
      from: formatLocalDate(today),
      to: formatLocalDate(horizon),
    }
  }, [])
  const activeMetricKey = useMemo((): ContractMetricKey | undefined => {
    const hasDateRange = !!filters.end_date_from || !!filters.end_date_to
    const expiringOnly = filters.expiring === '1'
    if (filters.status === 'active' && !hasDateRange && !expiringOnly)
      return 'active'
    if (
      filters.status === 'pending_signature' &&
      !hasDateRange &&
      !expiringOnly
    )
      return 'pending'
    if (filters.status === 'expired' && !hasDateRange && !expiringOnly)
      return 'expired'
    if (expiringOnly && !filters.status && !hasDateRange) return 'renew'
    if (
      filters.end_date_from === renewWithin30DaysRange.from &&
      filters.end_date_to === renewWithin30DaysRange.to &&
      !filters.status &&
      !expiringOnly
    )
      return 'renew'
    return undefined
  }, [filters, renewWithin30DaysRange])
  const metricActions = useMemo<ContractMetricActions>(
    () => ({
      active: () =>
        setFilters({
          status: 'active',
          end_date_from: undefined,
          end_date_to: undefined,
          expiring: undefined,
        }),
      pending: () =>
        setFilters({
          status: 'pending_signature',
          end_date_from: undefined,
          end_date_to: undefined,
          expiring: undefined,
        }),
      renew: () =>
        setFilters({
          status: undefined,
          end_date_from: renewWithin30DaysRange.from,
          end_date_to: renewWithin30DaysRange.to,
          expiring: undefined,
        }),
      expired: () =>
        setFilters({
          status: 'expired',
          end_date_from: undefined,
          end_date_to: undefined,
          expiring: undefined,
        }),
    }),
    [setFilters, renewWithin30DaysRange]
  )

  const [deleteTarget, setDeleteTarget] = useState<Contract | null>(null)
  const [terminateTarget, setTerminateTarget] = useState<Contract | null>(null)
  const [exportOpen, setExportOpen] = useState(false)

  // New Contract CTA lives in the shared TopBar (Figma 3396:160430).
  const newContractAction = useMemo(
    () => (
      <Permit module='contracts' action='edit'>
        <Button
          className={topBarNewContractClass}
          onClick={() => navigate('/contracts/new')}
        >
          <Plus className='size-4' />
          {t('list.newContract')}
        </Button>
      </Permit>
    ),
    [navigate, t]
  )
  useTopBarActions(newContractAction)

  return (
    <div className='flex flex-col gap-6'>
      <ContractMetricsSection
        summary={summary?.data}
        loading={summaryLoading}
        actions={metricActions}
        activeKey={activeMetricKey}
      />

      <ContractListFilters
        search={search}
        onSearchChange={onSearchChange}
        statuses={selectedStatuses}
        endDateFrom={filters.end_date_from}
        endDateTo={filters.end_date_to}
        onFiltersChange={setFilters}
        onExport={() => setExportOpen(true)}
      />

      <Card className='overflow-hidden rounded-2xl border border-[#e7e9ef] shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'>
        <div className='overflow-x-auto'>
          <table
            className='w-full table-fixed caption-bottom text-sm'
            style={{ minWidth: TABLE_MIN_WIDTH }}
          >
            <TableHeader className='sticky top-0 z-10 bg-[#fbfbfc]'>
              <TableRow className='border-0'>
                <TableHead className={cn(thClass, COL.customer)}>
                  {t('list.columns.customerName')}
                </TableHead>
                <TableHead className={cn(thClass, COL.title)}>
                  {t('list.columns.title')}
                </TableHead>
                <TableHead className={cn(thClass, COL.amount)}>
                  {t('list.columns.amountYear')}
                </TableHead>
                <TableHead className={cn(thClass, COL.status)}>
                  {t('list.columns.status')}
                </TableHead>
                <TableHead className={cn(thClass, COL.dueDate)}>
                  {t('list.columns.dueDate')}
                </TableHead>
                <TableHead className={cn(thClass, COL.frequency)}>
                  {t('list.columns.frequency')}
                </TableHead>
                <TableHead className={cn(thClass, COL.duration)}>
                  {t('list.columns.duration')}
                </TableHead>
                <TableHead className={cn(thClass, COL.contractNo)}>
                  {t('list.columns.contractNo')}
                </TableHead>
                <TableHead className={cn(thClass, COL.actions)}>
                  <div className={contractActionsGroupClass}>
                    {t('list.columns.actions')}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                      <Skeleton className='h-6 w-full' />
                    </TableCell>
                  </TableRow>
                ))}

              {isError && !isLoading && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      tdClass,
                      'py-10 text-center text-destructive'
                    )}
                  >
                    {t('list.loadError')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !isError && contracts.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      tdClass,
                      'py-10 text-center text-muted-foreground'
                    )}
                  >
                    {t('list.empty')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                !isError &&
                contracts.map(c => {
                  const canTerminate =
                    c.status === 'active' || c.status === 'to_be_renewed'
                  const canEdit = isContractEditable(c.status)
                  const canDelete =
                    c.status === 'draft' || c.status === 'declined'
                  const customerName = c.client?.name ?? c.client_name

                  return (
                    <TableRow
                      key={c.id}
                      className='group relative cursor-pointer'
                      onClick={() => navigate(`/contracts/${c.id}`)}
                    >
                      <TableCell className={cn(tdClass, COL.customer)}>
                        <span className='absolute inset-y-0 left-0 hidden w-1 bg-[#ff5a1f] group-hover:block' />
                        <div className='min-w-0 text-sm font-medium leading-[1.4] text-[#171a20] group-hover:text-[#ff5a1f]'>
                          <ClientNameWithProspect
                            name={customerName || EMPTY}
                            category={c.client?.category}
                          />
                        </div>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.title)}>
                        <TruncatedTextTooltip
                          className='line-clamp-2 max-w-[200px] text-sm leading-[1.4] text-[#171a20]'
                          content={c.title?.trim() || undefined}
                        >
                          {c.title?.trim() || EMPTY}
                        </TruncatedTextTooltip>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.amount)}>
                        <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                          {c.amount == null
                            ? EMPTY
                            : formatCurrency(c.amount, 'EUR', 2)}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.status)}>
                        <ListStatusBadge contract={c} />
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.dueDate)}>
                        <span className='whitespace-nowrap text-sm leading-[1.4] text-[#171a20]'>
                          {c.end_date ? formatDate(c.end_date) : EMPTY}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.frequency)}>
                        <span className='text-sm leading-[1.4] text-[#171a20]'>
                          {frequencyLabel(c.frequency)}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.duration)}>
                        <span className='text-sm leading-[1.4] text-[#171a20]'>
                          {durationLabel(c.start_date, c.end_date, t)}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.contractNo)}>
                        <TruncatedTextTooltip
                          className='line-clamp-2 max-w-[140px] text-sm font-medium leading-[1.4] text-[#171a20]'
                          content={c.number || undefined}
                        >
                          {c.number || EMPTY}
                        </TruncatedTextTooltip>
                      </TableCell>
                      <TableCell
                        className={cn(tdClass, COL.actions)}
                        onClick={event => event.stopPropagation()}
                      >
                        <div className={contractActionsGroupClass}>
                          <TruncatedTextTooltip
                            content={t('list.rowActions.view')}
                            delayDuration={0}
                            alwaysShow
                            closeOnPointerDown
                            className='inline-flex shrink-0'
                          >
                            <button
                              type='button'
                              className={actionBtnClass}
                              aria-label={t('list.rowActions.view')}
                              onClick={() => navigate(`/contracts/${c.id}`)}
                            >
                              <Eye className='size-4' strokeWidth={1.75} />
                            </button>
                          </TruncatedTextTooltip>
                          {canEdit && (
                            <Permit module='contracts' action='edit'>
                              <TruncatedTextTooltip
                                content={t('list.rowActions.edit')}
                                delayDuration={0}
                                alwaysShow
                                closeOnPointerDown
                                className='inline-flex shrink-0'
                              >
                                <button
                                  type='button'
                                  className={actionBtnClass}
                                  aria-label={t('list.rowActions.edit')}
                                  onClick={e => {
                                    e.stopPropagation()
                                    navigate(`/contracts/${c.id}/edit`)
                                  }}
                                >
                                  <SquarePen
                                    className='size-4'
                                    strokeWidth={1.75}
                                  />
                                </button>
                              </TruncatedTextTooltip>
                            </Permit>
                          )}
                          {canTerminate && (
                            <Permit module='contracts' action='edit'>
                              <TruncatedTextTooltip
                                content={t('list.rowActions.terminate')}
                                delayDuration={0}
                                alwaysShow
                                closeOnPointerDown
                                className='inline-flex shrink-0'
                              >
                                <button
                                  type='button'
                                  className={cn(
                                    actionBtnClass,
                                    destructiveActionBtnClass
                                  )}
                                  aria-label={t('list.rowActions.terminate')}
                                  onClick={e => {
                                    e.stopPropagation()
                                    setTerminateTarget(c)
                                  }}
                                >
                                  <XCircle
                                    className='size-4'
                                    strokeWidth={1.75}
                                  />
                                </button>
                              </TruncatedTextTooltip>
                            </Permit>
                          )}
                          {canDelete && (
                            <Permit module='contracts' action='delete'>
                              <TruncatedTextTooltip
                                content={t('list.rowActions.delete')}
                                delayDuration={0}
                                alwaysShow
                                closeOnPointerDown
                                className='inline-flex shrink-0'
                              >
                                <button
                                  type='button'
                                  className={cn(
                                    actionBtnClass,
                                    destructiveActionBtnClass
                                  )}
                                  aria-label={t('list.rowActions.delete')}
                                  onClick={e => {
                                    e.stopPropagation()
                                    setDeleteTarget(c)
                                  }}
                                >
                                  <Trash2
                                    className='size-4'
                                    strokeWidth={1.75}
                                  />
                                </button>
                              </TruncatedTextTooltip>
                            </Permit>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
            </TableBody>
          </table>
        </div>

        <ListPagination
          className='shrink-0'
          metadata={data?.metadata}
          page={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
          showShowingRange={false}
        />
      </Card>

      <DeleteContractDialog
        contract={deleteTarget}
        onOpenChange={open => !open && setDeleteTarget(null)}
        onDeleted={() => setDeleteTarget(null)}
      />

      <TerminateContractDialog
        key={terminateTarget?.id ?? 'closed'}
        contract={terminateTarget}
        onOpenChange={open => !open && setTerminateTarget(null)}
        onTerminated={() => setTerminateTarget(null)}
      />

      {/* List export (feedback #55): honours the active search/status/date
          filters; exports as .xlsx / .csv named Contract_list_[YYYY-MM-DD]. */}
      <ListExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        entityLabel='Contract'
        rowCount={totalRows}
        onExport={async format => {
          const res = await contractsApi.export({ ...listQuery, format })
          return {
            url: res.data!.url,
            filename: res.data!.filename,
          }
        }}
      />

      {/* Status legend reference (feedback #52) kept for screen readers. */}
      <p className='sr-only'>
        {Object.values(CONTRACT_STATUS_LABELS).join(', ')}
      </p>
    </div>
  )
}
